import { useRef, useState } from 'react';
import type { ProfileRow } from '../data/db';
import type { Animal, Color } from '../data/looks';
import { AnimalIcon, COLOR_LABELS, LABELS } from './animals';
import { ConfirmDialog } from './ConfirmDialog';
import { PasswordPrompt } from './PasswordPrompt';
import { Hint, Notice, Screen, SecondaryButton, Title } from './kit';

interface Props {
  profiles: ProfileRow[];
  /** `null` — взрослый занимается без учётной записи. Не пустая строка:
   *  пустая строка выглядит на экране как «запись есть, адрес потерялся», а
   *  это другое положение дел и другой набор кнопок. */
  email: string | null;
  onSignIn: () => void;
  /** Одна строка о состоянии обмена, или `null`, когда всё в порядке.
   *  Постоянная зелёная галочка учит не смотреть на это место. */
  syncText: string | null;
  unsent: number;
  storagePersisted: boolean | null;
  onEdit: (profileId: string) => void;
  onExport: (profileId: string) => void;
  /** Пароль `null` — подтверждения паролем не было, потому что учётной
   *  записи нет и пароля не существует. */
  onDelete: (profileId: string, password: string | null) => void;
  onImport: (file: File) => void;
  onSyncNow: () => void;
  onReread: () => void;
  onRetrySend: () => void;
  onSignOut: (password: string, everywhere: boolean) => void;
  onDeleteAccount: (password: string) => void;
  onPolicy: () => void;
  onTerms: () => void;
  onBack: () => void;
  notice?: string | null;
  error?: string | null;
  /** Отказ ввода пароля. Отдельно от общей ошибки: показывается только в
   *  окне пароля. Живёт он при этом у маршрута, а не в окне, и с закрытием
   *  окна сам не исчезает — снимается сигналом `onPasswordPrompt`, когда
   *  открывается следующее окно. */
  passwordError?: string | null;
  busy?: boolean;
  /** Открывается НОВОЕ окно пароля. Отказ живёт у маршрута
   *  (`passwordError`), а не в окне, и без этого сигнала отказ, полученный в
   *  одном окне и оставленный там отменой, всплывал в следующем — уже про
   *  другое действие (измерено: «неверный пароль» удаления записи в окне
   *  выхода, `tests/ui/accountDeletion.test.tsx`, карточка 3г). */
  onPasswordPrompt?: () => void;
}

const ACTION = 'min-h-11 rounded-lg border border-line bg-card px-3 py-2 text-sm text-ink';

export function look(profile: ProfileRow): string {
  return `${COLOR_LABELS[profile.color as Color]} ${LABELS[profile.animal as Animal]}`;
}

type Pending =
  | { kind: 'deleteProfile'; profile: ProfileRow }
  | { kind: 'signOutWarn'; everywhere: boolean }
  | { kind: 'signOut'; everywhere: boolean }
  | { kind: 'deleteAccount' };

/** Родительский раздел: профили детей, состояние обмена и всё необратимое.
 *
 *  Необратимое стоит за повторным вводом пароля, а не только за вопросом
 *  «А ты точно родитель?». Вход родителя происходит на детском устройстве и оставляет
 *  ключ надолго: один вход превращает планшет в доступ к журналам всех
 *  детей и к удалению учётной записи. */
export function ParentScreen({
  profiles, email, syncText, unsent, storagePersisted, onSignIn, onEdit, onExport, onDelete, onImport,
  onSyncNow, onReread, onRetrySend, onSignOut, onDeleteAccount, onPolicy, onTerms, onBack,
  notice, error, passwordError, busy, onPasswordPrompt,
}: Props) {
  const [pending, setPending] = useState<Pending | null>(null);
  /** Окна, где спрашивают пароль, открываются только отсюда: снятие
   *  прежнего отказа стоит на ОТКРЫТИИ, а не на отмене, и потому держит
   *  любой путь, которым прошлое окно закрылось, — отмену, Escape, ребёнка,
   *  пропавшего из списка. Удаление ребёнка без учётной записи окна пароля
   *  не открывает, но и снятие ему не вредит: отказа там нет. */
  const спроситьПароль = (окно: Pending) => {
    onPasswordPrompt?.();
    setPending(окно);
  };
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <Screen>
      <Title>Родителям</Title>
      {email === null ? (
        <p className="m-0 text-[0.95rem] text-muted">Без учётной записи</p>
      ) : (
        <p className="m-0 text-[0.95rem] text-muted">Учётная запись: {email}</p>
      )}

      <section className="flex flex-col gap-2 rounded-xl border border-line bg-card p-3">
        <h2 className="m-0 text-base font-semibold">Синхронизация</h2>
        {syncText ? (
          <p className="m-0 text-[0.95rem] text-ink">{syncText}</p>
        ) : (
          <p className="m-0 text-[0.95rem] text-muted">Всё отправлено.</p>
        )}
        {/* Значение пишется с первого дня и до сих пор никем не читалось. С
            появлением облака это ответ на вопрос «уедет ли это, если
            браузер вытеснит хранилище». */}
        {storagePersisted === false && (
          <p className="m-0 text-[0.95rem] text-muted">
            Браузер не обещает хранить данные долго. Установите приложение на
            устройство — тогда занятия не пропадут, даже если их долго не
            открывать.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" className={ACTION} disabled={busy} onClick={onSyncNow}>
            Синхронизировать сейчас
          </button>
          {/* Восстановление на случай, которого курсор не покрывает:
              строка применена, курсор ушёл вперёд, а потом местная строка
              пропала. Обычная докачка её не вернёт никогда. */}
          <button type="button" className={ACTION} disabled={busy} onClick={onReread}>
            Перечитать занятия заново
          </button>
        </div>
      </section>

      <h2 className="m-0 text-base font-semibold">Профили детей</h2>
      <Hint>Занятия каждого ребёнка видны только под его зверем.</Hint>

      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {profiles.map((profile) => (
          <li
            key={profile.id}
            className="flex flex-col gap-2 rounded-xl border border-line bg-card p-3"
          >
            <div className="flex items-center gap-3">
              <AnimalIcon
                animal={profile.animal as Animal}
                color={profile.color as Color}
                size={40}
              />
              <span className="font-semibold">{look(profile)}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={ACTION} onClick={() => onEdit(profile.id)}>
                Изменить облик
              </button>
              <button type="button" className={ACTION} onClick={() => onExport(profile.id)}>
                Выгрузить занятия
              </button>
              <button
                type="button"
                className={`${ACTION} text-incorrect`}
                onClick={() => спроситьПароль({ kind: 'deleteProfile', profile })}
              >
                Удалить навсегда
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div>
        <button type="button" className={ACTION} onClick={() => fileInput.current?.click()}>
          Загрузить занятия из файла
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          aria-label="Файл с занятиями"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            /** Значение поля сбрасывается, иначе повторный выбор ТОГО ЖЕ
             *  файла не даёт события `change` и кнопка выглядит сломанной. */
            event.target.value = '';
            if (file) onImport(file);
          }}
        />
      </div>

      {/* Итог действия объявляется вежливо, отказ — немедленно. Обычным
          абзацем ни то ни другое для диктора не существует: кнопка
          нажата, дальше тишина. */}
      <Notice text={notice} />
      {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}

      <section className="flex flex-col gap-2 rounded-xl border border-line bg-card p-3">
        <h2 className="m-0 text-base font-semibold">Учётная запись</h2>
        {email === null ? (
          <>
            {/* Без записи здесь всего одно действие. Выходить неоткуда,
                удалять на сервере нечего, а пароля, которым такие действия
                подтверждаются, не существует: он проверяется у сервера, а
                не сравнением с чем-то местным. */}
            <Hint>
              Занятия хранятся только на этом устройстве. Если браузер очистит хранилище
              или устройство потеряется, вернуть их будет нечем.
            </Hint>
            <button type="button" className={ACTION} onClick={onSignIn}>
              Войти или завести запись
            </button>
            <Hint>
              Всё, что дети уже прошли, при входе сохранится и уедет в вашу запись.
            </Hint>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={ACTION}
                onClick={() => setPending({ kind: 'signOutWarn', everywhere: false })}
              >
                Выйти
              </button>
              <button
                type="button"
                className={ACTION}
                onClick={() => setPending({ kind: 'signOutWarn', everywhere: true })}
              >
                Выйти на всех устройствах
              </button>
              <button
                type="button"
                className={`${ACTION} text-incorrect`}
                onClick={() => спроситьПароль({ kind: 'deleteAccount' })}
              >
                Удалить учётную запись
              </button>
            </div>
            <Hint>
              Выход стирает занятия с этого устройства: планшет может быть общим.
              Отправленное вернётся после следующего входа.
            </Hint>
          </>
        )}
        <button type="button" onClick={onPolicy} className="min-h-11 text-left text-[0.95rem] underline">
          Политика обработки данных
        </button>
        <button type="button" onClick={onTerms} className="min-h-11 text-left text-[0.95rem] underline">
          Условия использования
        </button>
      </section>

      <SecondaryButton bottom onClick={onBack}>
        Назад
      </SecondaryButton>

      {pending?.kind === 'signOutWarn' && (
        <ConfirmDialog
          title="Выйти из учётной записи?"
          /* Предупреждение показывается ВСЕГДА, а не только когда есть
             неотправленное.
             Прежде оно было условным — `unsent > 0`, — и ровно этим
             пропускало худший случай. Взрослый вошёл не в ту запись
             (свою прежнюю, супруга, чужую), обмен прошёл успешно, число
             неотправленного стало нулём, предупреждение молчит — и выход
             стирает единственную местную копию занятий, которые уехали не
             туда. Экран при этом здоров на всём пути.
             Цена называется числом, а не словами: «возможно, часть данных
             будет потеряна» родителю не говорит ничего. */
          body={
            unsent > 0
              ? `Ещё не отправлено тренировок: ${unsent}. Если выйти сейчас, они пропадут.`
              : 'Занятия детей будут стёрты с этого устройства. Из вашей учётной записи они никуда не денутся и вернутся при следующем входе.'
          }
          cancelLabel={unsent > 0 ? 'Попробовать ещё раз' : 'Отмена'}
          confirmLabel="Выйти"
          onCancel={() => {
            setPending(null);
            if (unsent > 0) onRetrySend();
          }}
          onConfirm={() => спроситьПароль({ kind: 'signOut', everywhere: pending.everywhere })}
        />
      )}

      {pending?.kind === 'deleteProfile' && email === null && (
        <ConfirmDialog
          title="Удалить навсегда?"
          body={`Занятия, которые прошёл ${look(pending.profile)}, будут удалены с этого устройства. Вернуть их будет нечем: копии в облаке нет, потому что вы не входили.`}
          cancelLabel="Отмена"
          confirmLabel="Удалить навсегда"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            /** Диалог закрывается САМ. Прежде он оставался на экране после
             *  удаления: профиля уже нет, а окно «Удалить навсегда?» с его
             *  именем висит и предлагает нажать ещё раз. */
            setPending(null);
            onDelete(pending.profile.id, null);
          }}
        />
      )}

      {/* Окно пароля живёт, пока ребёнок в списке, а не до нажатия.
          Закрывалось оно нажатием — так чинили окно, висевшее после
          удаления с именем уже удалённого ребёнка, — и вместе с ним
          пропадал единственный показ отказа: `passwordError` рисует только
          это окно. Родитель вводил не тот пароль, окно исчезало, ребёнок
          оставался на месте, и не было ни слова. Измерено исполнением
          (`tests/ui/App.test.tsx`, «неверный пароль при удалении ребёнка»).
          Теперь отказ — в окне, где вводили пароль, как у выхода и у
          удаления записи, а удаление закрывает окно само: ребёнка больше
          нет в списке. */}
      {pending?.kind === 'deleteProfile' && email !== null
        && profiles.some((profile) => profile.id === pending.profile.id) && (
        <PasswordPrompt
          title="Удалить навсегда?"
          body={`Занятия, которые прошёл ${look(pending.profile)}, будут удалены с этого устройства и из облака. Вернуть их будет нельзя.`}
          confirmLabel="Удалить навсегда"
          busy={busy}
          error={passwordError}
          onCancel={() => setPending(null)}
          onConfirm={(password) => onDelete(pending.profile.id, password)}
        />
      )}

      {pending?.kind === 'signOut' && (
        <PasswordPrompt
          title={pending.everywhere ? 'Выйти на всех устройствах' : 'Выйти из учётной записи'}
          /* Про чужое устройство сказано честно: у сервера нет канала
             дотянуться до планшета, которого нет в руках. Потерянный
             планшет с активной сессией продолжит показывать занятия, пока
             его не включат при интернете. */
          body={
            pending.everywhere
              ? 'Занятия будут стёрты с ЭТОГО устройства, а вход отозван на всех. На чужом устройстве занятия исчезнут не сразу: оно узнает об отзыве, когда выйдет в интернет.'
              : 'Занятия будут стёрты с этого устройства. Отправленное вернётся после следующего входа.'
          }
          confirmLabel="Выйти и стереть"
          busy={busy}
          error={passwordError}
          onCancel={() => setPending(null)}
          onConfirm={(password) => onSignOut(password, pending.everywhere)}
        />
      )}

      {pending?.kind === 'deleteAccount' && (
        <PasswordPrompt
          title="Удалить учётную запись"
          body="Будут удалены почта родителя, профили всех детей и все их занятия — и на сервере, и на этом устройстве. Вернуть их будет нельзя."
          confirmLabel="Удалить всё навсегда"
          busy={busy}
          error={passwordError}
          onCancel={() => setPending(null)}
          onConfirm={onDeleteAccount}
        />
      )}
    </Screen>
  );
}
